import { useEffect, useMemo, useState } from 'react';
import { adminApi } from '@/lib/api';
import { Loader2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Card, CardContent } from '@/components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Label } from '@/components/ui/label';
import { toast } from 'sonner';

const formatCurrency = (amount) => {
  return new Intl.NumberFormat('en-IN', {
    style: 'currency',
    currency: 'INR',
    maximumFractionDigits: 0
  }).format(amount || 0);
};

const normalizeList = (value) => {
  if (Array.isArray(value)) return value;
  if (Array.isArray(value?.items)) return value.items;
  return [];
};

export default function CreditNotesPage() {
  const [notes, setNotes] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const [form, setForm] = useState({
    customer_id: '',
    amount: '',
    date: '',
    reason: '',
    order_id: ''
  });

  const customerMap = useMemo(() => {
    const map = {};
    normalizeList(customers).forEach((c) => {
      map[c.id] = c.shop_name || c.name;
    });
    return map;
  }, [customers]);

  const loadData = async () => {
    setLoading(true);
    try {
      const [noteRes, customerRes] = await Promise.all([
        adminApi.getCreditNotes(),
        adminApi.getCustomers()
      ]);
      setNotes(normalizeList(noteRes.data));
      setCustomers(normalizeList(customerRes.data));
    } catch (error) {
      toast.error('Failed to load credit notes');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadData();
  }, []);

  const handleCreate = async () => {
    if (!form.customer_id || !form.amount || !form.reason) {
      toast.error('Customer, amount and reason are required');
      return;
    }
    setSaving(true);
    try {
      await adminApi.createCreditNote({
        ...form,
        amount: parseFloat(form.amount),
        date: form.date || undefined,
        order_id: form.order_id || undefined
      });
      toast.success('Credit note created');
      setForm({
        customer_id: '',
        amount: '',
        date: '',
        reason: '',
        order_id: ''
      });
      await loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create credit note');
    } finally {
      setSaving(false);
    }
  };

  const handlePost = async (id) => {
    try {
      await adminApi.postCreditNote(id);
      toast.success('Credit note posted');
      await loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to post credit note');
    }
  };

  return (
    <div className="space-y-6" data-testid="credit-notes-page">
      <div>
        <h1 className="text-3xl font-bold text-[#2D2420]">Credit Notes</h1>
        <p className="text-[#A8A8A8] mt-1">Issue credit notes for returns</p>
      </div>

      <Card className="bg-white border-[#E5E5E5]">
        <CardContent className="p-6 space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="space-y-2">
              <Label className="text-[#5C4033]">Customer</Label>
              <Select
                value={form.customer_id}
                onValueChange={(value) => setForm({ ...form, customer_id: value })}
              >
                <SelectTrigger className="border-[#E5E5E5]">
                  <SelectValue placeholder="Select" />
                </SelectTrigger>
                <SelectContent>
                  {normalizeList(customers).map((customer) => (
                    <SelectItem key={customer.id} value={customer.id}>
                      {customer.shop_name || customer.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label className="text-[#5C4033]">Amount</Label>
              <Input
                type="number"
                value={form.amount}
                onChange={(e) => setForm({ ...form, amount: e.target.value })}
                className="border-[#E5E5E5] focus:border-[#C97A5E]"
              />
            </div>
            <div className="space-y-2">
              <Label className="text-[#5C4033]">Date</Label>
              <Input
                type="date"
                value={form.date}
                onChange={(e) => setForm({ ...form, date: e.target.value })}
                className="border-[#E5E5E5] focus:border-[#C97A5E]"
              />
            </div>
            <div className="space-y-2 md:col-span-2">
              <Label className="text-[#5C4033]">Reason</Label>
              <Input
                value={form.reason}
                onChange={(e) => setForm({ ...form, reason: e.target.value })}
                className="border-[#E5E5E5] focus:border-[#C97A5E]"
              />
            </div>
            <div className="space-y-2">
              <Label className="text-[#5C4033]">Order ID (optional)</Label>
              <Input
                value={form.order_id}
                onChange={(e) => setForm({ ...form, order_id: e.target.value })}
                className="border-[#E5E5E5] focus:border-[#C97A5E]"
              />
            </div>
          </div>

          <div className="flex justify-end">
            <Button
              onClick={handleCreate}
              disabled={saving}
              className="bg-[#C97A5E] hover:bg-[#B06850]"
            >
              {saving ? 'Saving...' : 'Create Credit Note'}
            </Button>
          </div>
        </CardContent>
      </Card>

      <Card className="bg-white border-[#E5E5E5]">
        <CardContent className="p-0">
          {loading ? (
            <div className="flex items-center justify-center h-64">
              <Loader2 className="w-8 h-8 animate-spin text-[#C97A5E]" />
            </div>
          ) : notes.length === 0 ? (
            <div className="p-6 text-sm text-[#A8A8A8]">No credit notes yet.</div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-[#FAFAF9] border-b border-[#E5E5E5]">
                  <tr>
                    <th className="text-left py-3 px-4 text-xs font-semibold text-[#5C4033] uppercase">Credit Note No</th>
                    <th className="text-left py-3 px-4 text-xs font-semibold text-[#5C4033] uppercase">Customer</th>
                    <th className="text-left py-3 px-4 text-xs font-semibold text-[#5C4033] uppercase">Amount</th>
                    <th className="text-left py-3 px-4 text-xs font-semibold text-[#5C4033] uppercase">Date</th>
                    <th className="text-left py-3 px-4 text-xs font-semibold text-[#5C4033] uppercase">Status</th>
                    <th className="text-right py-3 px-4 text-xs font-semibold text-[#5C4033] uppercase">Action</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-[#E5E5E5]">
                  {notes.map((note) => (
                    <tr key={note.id}>
                      <td className="py-3 px-4 text-sm text-[#2D2420]">{note.credit_note_no}</td>
                      <td className="py-3 px-4 text-sm text-[#2D2420]">{customerMap[note.customer_id] || '-'}</td>
                      <td className="py-3 px-4 text-sm text-[#2D2420]">{formatCurrency(note.amount)}</td>
                      <td className="py-3 px-4 text-sm text-[#2D2420]">{note.date ? new Date(note.date).toLocaleDateString() : '-'}</td>
                      <td className="py-3 px-4 text-sm text-[#2D2420]">
                        <div>{note.status}</div>
                        {note.redeemed_at ? (
                          <div className="text-xs text-[#A8A8A8]">
                            Redeemed {new Date(note.redeemed_at).toLocaleDateString()}
                          </div>
                        ) : null}
                      </td>
                      <td className="py-3 px-4 text-right">
                        <div className="flex items-center justify-end gap-2">
                          {note.credit_note_url && (
                            <Button
                              variant="outline"
                              onClick={() => window.open(note.credit_note_url, '_blank')}
                              className="border-[#E5E5E5]"
                            >
                              Download
                            </Button>
                          )}
                          {note.status === 'DRAFT' ? (
                            <Button
                              variant="outline"
                              onClick={() => handlePost(note.id)}
                              className="border-[#E5E5E5]"
                            >
                              Post
                            </Button>
                          ) : (
                            <span className="text-xs text-[#A8A8A8]">
                              {note.status === 'REDEEMED' ? 'Redeemed' : 'Posted'}
                            </span>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
